/**
 * @fileoverview BiometricAuthButton Component for TimeFiles
 *
 * Reusable button for passkey authentication (Touch ID, Face ID, Windows Hello).
 * Handles the authentication flow and displays appropriate UI states with automatic
 * platform capability detection.
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Shield } from 'lucide-react';
import { isWebAuthnSupported, isPlatformAuthenticatorAvailable } from '../utils/webauthn';
import '../styles/BiometricAuth.css';

const BiometricAuthButton = ({ onSuccess, onError, disabled = false, username = '' }) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const checkSupport = async () => {
      const supported = isWebAuthnSupported();
      setIsSupported(supported);

      if (supported) {
        const available = await isPlatformAuthenticatorAvailable();
        setIsAvailable(available);
      }
    };

    checkSupport();
  }, []);

  const handleClick = async () => {
    if (disabled || isLoading) return;

    setIsLoading(true);

    try {
      await onSuccess();
    } catch (error) {
      if (onError) {
        onError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isSupported || !isAvailable) {
    return null;
  }

  return (
    <button
      className="biometric-auth-button"
      onClick={handleClick}
      disabled={disabled || isLoading}
      type="button"
    >
      <Shield className="biometric-icon" />
      <span className="biometric-text">
        {isLoading ? 'Authenticating...' : 'Use Passkey'}
      </span>
    </button>
  );
};

BiometricAuthButton.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func,
  disabled: PropTypes.bool,
  username: PropTypes.string
};

BiometricAuthButton.defaultProps = {
  onError: null,
  disabled: false,
  username: ''
};

export default BiometricAuthButton;
